import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { Splide, SplideSlide } from '@splidejs/react-splide';

import ImageIcon from '@/icons/light/image.svg';
import PlayIcon from '@/icons/light/play.svg';
import { BackgroundImage, Button, Container } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';
import { Grid } from '@/molecules';

import '@splidejs/react-splide/css/core';

import Generic from '@/assets/images/generic.jpg';
import { flatName } from '@/components/algolia/utils';
import { formatNumberToCurrency } from '@/utils';

import type { TypeFlatAnalytics } from '@/types';
import type { FunctionComponent } from 'react';
import type { TypeFlatSheetProps } from './types';

const ModalGallery = dynamic(() => import('@/atoms/modal-gallery'), {
  ssr: true,
});
const ModalForm = dynamic(() => import('@/molecules/modal-form'), {
  ssr: true,
});
const FormsTabs = dynamic(() => import('@/organisms/forms-tabs'), {
  ssr: true,
});

/**
 * Variante hero (HoM) de /vivienda/[id], gated por
 * `viviendaPageStyle === 'hero'`. FlatSheet renderiza FlatDescription +
 * sidebar de forms después de este componente.
 */
export const FlatSheetHero: FunctionComponent<TypeFlatSheetProps> = ({
  property,
}) => {
  const [isGalleryOpen, setGalleryOpen] = useState(false);
  const [isVideoOpen, setVideoOpen] = useState(false);
  const [isContactOpen, setContactOpen] = useState(false);
  const [showInfoBar, setShowInfoBar] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowInfoBar(window.scrollY > 300);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const altText = property
    ? `${property?.tipoDeUsoDescription} de ${property?.habitaciones} ${
        (property?.habitaciones as number) > 1 ? 'habitaciones' : 'habitación'
      } en ${
        (property?.ciudad as string)?.[0]?.toUpperCase() +
        (property?.ciudad as string)?.substring(1).toLowerCase()
      }`
    : 'Vivienda';

  // Local-only escape hatch — ver comentario en card.ts.
  const FORCE_FALLBACK =
    process.env.NEXT_PUBLIC_FORCE_PHOTO_FALLBACK === 'true';
  const photos = FORCE_FALLBACK
    ? ([] as { src: string; alt: string }[])
    : (((property?.fotos ?? []) as any[])
        .map((itm: any) => (itm?.url ? { src: itm.url, alt: altText } : null))
        .filter(Boolean) as { src: string; alt: string }[]);

  const heroImage = photos[0]?.src ?? Generic.src;
  const sliderPhotos =
    photos.length > 1
      ? photos.slice(1)
      : [{ src: Generic.src, alt: `${altText} — placeholder` }];

  const flatAnalytics = {
    codigoUnidadAlquiler: property?.codigoUnidadAlquiler as string,
    ventaAlquiler:
      property?.ventaAlquiler as TypeFlatAnalytics['ventaAlquiler'],
    reservable: property?.reservable,
    visitable: property?.visitable,
    nombreUE: property?.nombreUE as string,
    cityArea: property?.tipoZona?.infoAdicional,
    name: flatName(property),
    maxOcupantes: property?.maxOcupantes,
  } as TypeFlatAnalytics;

  const hasVideo = !!property?.video?.infoAdicional;
  const srcVideo = property?.video?.infoAdicional as string | undefined;

  const isForSale = property?.ventaAlquiler?.toLowerCase() === 'venta';
  const priceText = formatNumberToCurrency(
    isForSale
      ? (property?.importeVenta as number)
      : (property?.importeRecibofinal as number),
  );

  return (
    <>
      <div
        className="border-input-border fixed top-0 right-0 left-0 z-20 hidden border-b bg-[var(--page-bgcolor,#fff)] transition-transform duration-200 lg:block"
        style={{
          transform: showInfoBar
            ? 'translateY(var(--header-height))'
            : 'translateY(-100%)',
        }}
        aria-hidden={!showInfoBar}
      >
        <Container wrapper>
          <div className="flex flex-row items-center gap-4 py-3">
            <h2 className="text-headline text-headline-h4 text-text-primary line-clamp-1 flex-1">
              {flatName(property)}
            </h2>
            <div className="flex items-center gap-x-6 text-sm">
              <div className="divide-neutrals-200 text-text-primary flex items-center divide-x">
                {property?.metrosComputablesUA && (
                  <span className="px-4 first:pl-0 last:pr-0">
                    {property.metrosComputablesUA as string} m²
                  </span>
                )}
                {property?.habitaciones && (
                  <span className="px-4 first:pl-0 last:pr-0">
                    {property.habitaciones as string} Habs.
                  </span>
                )}
                {property?.banos && (
                  <span className="px-4 first:pl-0 last:pr-0">
                    {property.banos as string} baños
                  </span>
                )}
              </div>
              <strong className="text-text-primary text-base">
                {priceText}
              </strong>
              <Button
                size={ButtonSize.SMALL}
                variant={ButtonVariant.SECONDARY}
                className="w-max"
                onClick={() => setContactOpen(true)}
              >
                Contactar ahora
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <section className="relative h-[80vh] min-h-[480px] w-full overflow-hidden">
        <Image
          src={heroImage}
          alt={altText}
          fill
          priority
          sizes="100vw"
          className="object-cover"
        />
        <div className="from-neutrals-900/70 via-neutrals-900/20 absolute inset-0 bg-gradient-to-b to-transparent" />
        <div className="from-neutrals-900/60 via-neutrals-900/20 absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t to-transparent" />
        <div className="absolute inset-x-0 bottom-12 lg:bottom-20">
          <div className="u-wrapper mx-auto w-full max-w-(--wrapper-size) px-4">
            <h1 className="text-headline text-headline-h1 text-neutrals-0">
              {flatName(property)}
            </h1>
          </div>
        </div>
      </section>

      <div className="flat-hero-slider relative mt-6 hidden pl-[160px] lg:mt-10 lg:block">
        <Splide
          options={{
            fixedWidth: '480px',
            gap: '40px',
            pagination: false,
            arrows: true,
            drag: true,
          }}
          aria-label={`Galería de fotos: ${altText}`}
        >
          {sliderPhotos.map((img, i) => (
            <SplideSlide key={`gallery-${i}`}>
              <button
                type="button"
                onClick={() => setGalleryOpen(true)}
                className="rounded-custom relative block aspect-[600/320] w-full cursor-pointer overflow-hidden"
                aria-label={`Ver foto ${i + 2} de ${photos.length}`}
              >
                <BackgroundImage backgroundImage={img.src} />
              </button>
            </SplideSlide>
          ))}
        </Splide>
      </div>

      <Container wrapper className="mt-6 lg:mt-5">
        <div className="flex justify-end gap-4">
          {hasVideo && (
            <Button
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.OUTLINE}
              className="gap-x-2! rounded-[12px]! font-bold!"
              onClick={() => setVideoOpen(true)}
            >
              Ver video <PlayIcon className="h-4 w-4" />
            </Button>
          )}
          <Button
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.OUTLINE}
            className="gap-x-2! rounded-[12px]! font-bold!"
            onClick={() => setGalleryOpen(true)}
          >
            Ver fotos <ImageIcon className="h-4 w-4" />
          </Button>
        </div>
      </Container>

      <ModalGallery
        closeModal={() => setGalleryOpen(false)}
        isOpen={isGalleryOpen}
        className="lg:rounded-custom u-wrapper scrollbar-hide bg-neutrals-0 h-full overflow-y-auto"
        isViewForms
        flat={flatAnalytics}
      >
        <div className="flex h-full w-full justify-between gap-6 lg:mb-4">
          <div className="flex w-full flex-col lg:gap-2">
            <Grid xxs={1} md={2} className="gap-y-2 md:gap-2">
              {(photos.length
                ? photos
                : [{ src: Generic.src, alt: altText }]
              ).map((img, i) => (
                <Grid.Item
                  className="md:nth-[3n+1]:col-span-2"
                  key={`modal-${i}`}
                >
                  <div className="relative h-full min-h-[300px] w-full">
                    <BackgroundImage backgroundImage={img.src} />
                  </div>
                </Grid.Item>
              ))}
            </Grid>
          </div>
          <div className="rounded-custom bg-neutrals-50 top-16 hidden h-full min-w-[368px] flex-col gap-6 p-6 lg:sticky lg:flex">
            <h3 className="md:text-headline md:text-headline-h4">
              Contacta con nosotros
            </h3>
            <FormsTabs flat={flatAnalytics} />
          </div>
        </div>
      </ModalGallery>

      {hasVideo && srcVideo && (
        <ModalGallery
          closeModal={() => setVideoOpen(false)}
          isOpen={isVideoOpen}
          className="lg:rounded-custom u-wrapper scrollbar-hide bg-neutrals-0 h-full overflow-y-auto"
          flat={flatAnalytics}
        >
          <div className="relative aspect-video w-full">
            <iframe
              src={srcVideo}
              className="absolute inset-0 h-full w-full"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              title={`Video de ${altText}`}
            />
          </div>
        </ModalGallery>
      )}

      <ModalForm
        isOpen={isContactOpen}
        closeModal={() => setContactOpen(false)}
      >
        <FormsTabs flat={flatAnalytics} noTitle />
      </ModalForm>
    </>
  );
};

FlatSheetHero.displayName = 'FlatSheetHero';
